import type {
  FetchAllTicketSaleTargetListParams,
  FetchTicketSaleListParams,
  FetchTicketSaleTargetListParams,
  TicketSale,
  TicketSaleTarget
} from 'Services/SalesManagement/Features/TicketSale/types'
import type { ListResponse } from 'Services/SalesManagement/types'
import { createBaseApi } from 'Util/ApiClient'

enum TagTypes {
  TicketSale = 'TicketSale',
  TicketSaleTarget = 'TicketSaleTarget'
}

export const ticketSaleApi = createBaseApi({
  reducerPath: 'ticketSaleApi',
  keepUnusedDataFor: 10,
  tagTypes: [TagTypes.TicketSale, TagTypes.TicketSaleTarget]
}).injectEndpoints({
  endpoints: builder => ({
    getTicketSales: builder.query<ListResponse<TicketSale>, FetchTicketSaleListParams | undefined>({
      query: params => ({
        url: '/api/v1/ticket-sales/group-tickets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.TicketSale, id })),
              { type: TagTypes.TicketSale, id: 'PARTIAL-LIST' }
            ]
          : [{ type: TagTypes.TicketSale, id: 'PARTIAL-LIST' }]
    }),
    getSingleTicketSale: builder.query<TicketSale, string>({
      query: id => `/api/v1/ticket-sales/group-tickets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TicketSale, id }]
    }),
    createSingleTicketSale: builder.mutation<TicketSale, Partial<TicketSale>>({
      query: data => ({
        url: `/api/v1/ticket-sales/group-tickets/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.TicketSale, id: 'LIST' },
        { type: TagTypes.TicketSale, id: 'PARTIAL-LIST' }
      ]
    }),
    sendTicketSaleEmailReceipt: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/ticket-sales/group-tickets/${id}/receipt/`,
        method: 'POST'
      })
    }),
    getTicketSaleTargets: builder.query<
      ListResponse<TicketSaleTarget>,
      FetchTicketSaleTargetListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/ticket-sales/targets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: TagTypes.TicketSaleTarget, id })),
              { type: TagTypes.TicketSaleTarget, id: 'PARTIAL-LIST' }
            ]
          : [{ type: TagTypes.TicketSaleTarget, id: 'PARTIAL-LIST' }]
    }),
    getAllTicketSaleTargets: builder.query<
      TicketSaleTarget[],
      FetchAllTicketSaleTargetListParams | undefined
    >({
      query: params => ({
        url: '/api/v1/ticket-sales/targets/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.TicketSaleTarget, id })),
              { type: TagTypes.TicketSaleTarget, id: 'LIST' }
            ]
          : [{ type: TagTypes.TicketSaleTarget, id: 'LIST' }]
    }),
    getSingleTicketSaleTarget: builder.query<TicketSaleTarget, string>({
      query: id => `/api/v1/ticket-sales/targets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TicketSaleTarget, id }]
    }),
    createSingleTicketSaleTarget: builder.mutation<TicketSaleTarget, Partial<TicketSaleTarget>>({
      query: data => ({
        url: `/api/v1/ticket-sales/targets/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.TicketSaleTarget, id: 'LIST' },
        { type: TagTypes.TicketSaleTarget, id: 'PARTIAL-LIST' }
      ]
    }),
    updateSingleTicketSaleTarget: builder.mutation<TicketSaleTarget, Partial<TicketSaleTarget>>({
      query: data => ({
        url: `/api/v1/ticket-sales/targets/${data.id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.TicketSaleTarget, id },
        { type: TagTypes.TicketSaleTarget, id: 'LIST' },
        { type: TagTypes.TicketSaleTarget, id: 'PARTIAL-LIST' }
      ]
    })
  })
})

export const {
  useGetTicketSalesQuery,
  useGetSingleTicketSaleQuery,
  useCreateSingleTicketSaleMutation,
  useSendTicketSaleEmailReceiptMutation,
  useGetTicketSaleTargetsQuery,
  useGetAllTicketSaleTargetsQuery,
  useGetSingleTicketSaleTargetQuery,
  useCreateSingleTicketSaleTargetMutation,
  useUpdateSingleTicketSaleTargetMutation
} = ticketSaleApi
